import { isPresignedMediaUrl, mediaCacheUrlKey } from "./media-url-key.ts";

/**
 * Pure decisions for the player service worker (`src/player/sw/sw.ts`).
 * Extracted so the cache-key/206 rules are node-testable and shared with the
 * page-side offline store instead of being duplicated inside the worker.
 */

export type MediaRequestPlan = {
  /** Cache Storage key the entry lives under (query-less for presigned URLs). */
  cacheKey: string;
  /** Whether cache.match must ignore the (rotating presigned) query string. */
  ignoreSearch: boolean;
};

const MEDIA_PATH_RE = /\.(mp3|m4a|aac|ogg|wav|mp4|webm|jpg|jpeg|png|webp|gif)$/i;

/**
 * Whether a request is player media the worker serves cache-first, and how to
 * key it. Returns null for everything the worker must pass through untouched.
 * Media is matched by file extension OR presigned signature because R2 presigns
 * can carry extension-less object paths.
 */
export function planMediaRequest(
  method: string,
  url: string,
): MediaRequestPlan | null {
  if (method !== "GET") return null;
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    return null;
  }
  const presigned = isPresignedMediaUrl(url);
  if (!MEDIA_PATH_RE.test(pathname) && !presigned) return null;
  return { cacheKey: mediaCacheUrlKey(url), ignoreSearch: presigned };
}

/**
 * Whether the PAGE PREFETCH may write a fetched media response to the cache.
 * NEVER cache 206 partial responses — a cached partial poisons later full
 * reads (documented invariant, docs/player-offline.md). Opaque (no-cors)
 * responses report status 0 and are accepted HERE ONLY: the prefetch fetches
 * URLs freshly minted by the payload it just received, so the odds of an
 * opaque body being an error page are as low as they get, and origins
 * without a CORS rule have no other way into the cache.
 */
export function shouldCacheMediaResponse(res: {
  status: number;
  ok: boolean;
  type: string;
}): boolean {
  if (res.status === 206) return false;
  return res.ok || res.type === "opaque";
}

/**
 * Whether the SERVICE WORKER may write its on-the-fly refetch to the cache:
 * verified 2xx only, never opaque. The worker refetches whatever URL the
 * media element asked for — including presigns that already EXPIRED — and an
 * opaque response hides the status, so accepting opaque here cached R2 403
 * bodies under the query-less key. Every later request (with a fresh, valid
 * signature) then matched the poisoned entry cache-first and the asset was
 * dead until a manual cache clear.
 */
export function shouldCacheWorkerMediaResponse(res: {
  status: number;
  ok: boolean;
}): boolean {
  return res.ok && res.status !== 206;
}
